import { useLoggedInUser } from "@coworker/app/src/core/auth/useLoggedInUser";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";
import statusTypes from "@coworker/enums/statusTypes";
import { HttpError } from "@coworker/http-client";
import React from "react";
import { useAtkomstRoles } from "../../login/hooks/useAtkomstRoles";
import { useUpsertUserByEmail } from "../../login/hooks/useUpsertUserByEmail";
import { ErrorType } from "../constants/errorTypes";
import { useTeamQuery } from "./useTeamQuery";
import { useUserFromMsal } from "./useUserFromMsal";

type UserState = {
  isActive: boolean;
  isLoading: boolean;
  errorType: ErrorType | null;
  isError: boolean;
};

type UseAuthorizeUserResult = UserState & {
  isAuthorizing: boolean;
};

export const useAuthorizeUser = (enabled = true): UseAuthorizeUserResult => {
  const { uid, email } = useUserFromMsal();
  const isQueryEnabled = !!email && !!uid;

  const { setLoggedInUser, resetLoggedInUser } = useLoggedInUser();

  const initialUserState = {
    isActive: false,
    isLoading: true,
    errorType: null,
    isError: false,
  } as UserState;
  const [userState, setUserState] = React.useState(initialUserState);

  const {
    data: userData,
    isLoading: userIsLoading,
    isFetching: userIsFetching,
    isError,
    error,
  } = useUpsertUserByEmail(email, uid, enabled);
  const userDataIsLoading = userIsLoading || userIsFetching;

  const enableAtkomst = useFlag(FLAGS.ENABLE_ATKOMST);
  const {
    data: atkomstRoles,
    isLoading: atkomstRolesIsLoading,
    isFetching: atkomstRolesIsFetching,
  } = useAtkomstRoles(
    {
      email,
      storeId: userData?.store?.storeId,
      storeCountryId: userData?.store?.countryId,
    },
    { enabled: enableAtkomst && Boolean(userData) }
  );

  const atkomstRolesIsLoadingOrFetching =
    atkomstRolesIsLoading || atkomstRolesIsFetching;

  const {
    data: teamData,
    isLoading: isTeamLoading,
    isFetching: isTeamFetching,
  } = useTeamQuery(userData?.team_id);
  const teamIsLoading = isTeamLoading || isTeamFetching;

  const isLoading =
    userDataIsLoading || atkomstRolesIsLoadingOrFetching || teamIsLoading;
  const isAuthorizing = enabled && isLoading;

  React.useEffect(() => {
    if (!enabled) {
      setUserState({
        isActive: false,
        errorType: null,
        isLoading: false,
        isError: false,
      });
      return;
    }

    if (!isQueryEnabled) return;
    if (!isLoading && !isError) {
      const hasStoreAssigned = Boolean(userData?.store_id);

      if (
        userData &&
        userData.status === statusTypes.ACTIVE &&
        hasStoreAssigned
      ) {
        setUserState({
          isActive: true,
          errorType: null,
          isLoading: false,
          isError: false,
        });

        // if team does not exist, set team_id to empty string to avoid errors in other parts of the app.
        // The useEnsureUserTeamMembership hook will redirect to the team selection page
        if (!teamData) {
          setLoggedInUser({
            ...userData,
            atkomstRoles: atkomstRoles ?? [],
            team_id: "",
          });
          return;
        }

        setLoggedInUser({
          ...userData,
          atkomstRoles: atkomstRoles ?? [],
        });
      } else if (userData && userData.status !== statusTypes.ACTIVE) {
        // User exists in DB but is INACTIVE — should not be allowed to self-provision
        setUserState({
          isActive: false,
          errorType: ErrorType.FORBIDDEN,
          isLoading: false,
          isError: false,
        });
        resetLoggedInUser();
      } else {
        // User has no store assigned yet or doesn't exist — needs self-provisioning
        setUserState({
          isActive: false,
          errorType: ErrorType.NOTFOUND,
          isLoading: false,
          isError: false,
        });
        resetLoggedInUser();
      }
    } else if (isError) {
      const httpStatus = error instanceof HttpError ? error.status : null;
      const errorType =
        httpStatus === 403
          ? ErrorType.FORBIDDEN
          : httpStatus === 404
            ? ErrorType.NOTFOUND
            : httpStatus === 401
              ? ErrorType.UNAUTHORIZED
              : ErrorType.INTERNALERROR;
      setUserState({
        isActive: false,
        errorType,
        isLoading: false,
        isError: true,
      });
      resetLoggedInUser();
    }
  }, [
    isLoading,
    userData,
    setLoggedInUser,
    isError,
    error,
    resetLoggedInUser,
    atkomstRoles,
    enabled,
    isQueryEnabled,
    teamData,
  ]);
  return {
    ...userState,
    isAuthorizing,
  };
};
